import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme/tokens';

interface TrackNowTableColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string;
  clickable?: boolean;
  renderCell?: (value: any, row: any) => React.ReactNode;
}

interface TrackNowTableProps {
  columns: TrackNowTableColumn[];
  rows: any[];
  onCellClick?: (columnId: string, value: any, row: any) => void;
  compact?: boolean;
}

export const TrackNowTable: React.FC<TrackNowTableProps> = ({
  columns,
  rows,
  onCellClick,
  compact = false,
}) => {
  const theme = useTheme();
  const [activeCell, setActiveCell] = useState<string | null>(null);

  const handleCellClick = (columnId: string, value: any, row: any, rowIndex: number) => {
    const cellKey = `${rowIndex}-${columnId}`;
    setActiveCell(cellKey);
    if (onCellClick) {
      onCellClick(columnId, value, row);
    }
  };

  // Click outside to deselect
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveCell(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.colors.grey}`,
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: tokens.colors.offWhite,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <TableContainer sx={{ maxHeight: "500px", overflow: "auto" }}>
        <Table>
          {/* Header */}
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: tokens.typography.fontFamily,
                    padding: compact ? '10px 16px' : undefined,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    borderBottom: 'none',
                    '&:last-child': {
                      borderRight: 'none',
                    },
                    minWidth: column.minWidth,
                    verticalAlign: 'middle',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Body */}
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  '&:hover': {
                    backgroundColor: tokens.colors.midGrey + '20',
                  },
                }}
              >
                {columns.map((column) => {
                  const value = row[column.id];
                  const cellKey = `${rowIndex}-${column.id}`;
                  const isActive = activeCell === cellKey;

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      sx={{
                        borderRight: `1px solid ${tokens.colors.grey}`,
                        borderBottom: 'none',
                        backgroundColor: tokens.colors.offWhite,
                        padding: compact ? '4px 16px' : '12px 16px',
                        fontSize: '14px',
                        fontFamily: tokens.typography.fontFamily,
                        verticalAlign: 'middle',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                      }}
                    >
                      {column.renderCell ? (
                        column.renderCell(value, row)
                      ) : (
                        <Box
                          sx={{
                            color: isActive ? theme.palette.primary.main : tokens.colors.offBlack,
                            fontWeight: isActive ? 700 : 400,
                            cursor: column.clickable ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': column.clickable ? {
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                            } : {},
                          }}
                          onClick={
                            column.clickable
                              ? (e) => {
                                e.stopPropagation();
                                handleCellClick(column.id, value, row, rowIndex);
                              }
                              : undefined
                          }
                        >
                          {column.format ? column.format(value) : value}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
